import type { ChatContextPayload, ChatSource } from "@/lib/chat-context";

export interface WebSource {
  n: number;
  title: string;
  url: string;
  snippet?: string;
  date?: string;
}

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[];
  webSources?: WebSource[];
  suggestions?: string[];
  pending?: boolean;
  error?: string;
}

export interface ChatResponse {
  answer: string;
  sources: ChatSource[];
  webSources?: WebSource[];
  suggestions?: string[];
  citations?: string[];
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number } | null;
  cap?: { count: number; daily: number; remaining: number };
  model?: string;
  email?: string;
}

export interface ChatErrorBody {
  error: string;
  detail?: string;
  count?: number;
  cap?: number;
  status?: number;
}

export async function sendChat(payload: ChatContextPayload, signal?: AbortSignal): Promise<ChatResponse> {
  const res = await fetch("/api/chat", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    let body: ChatErrorBody | null = null;
    try {
      body = (await res.json()) as ChatErrorBody;
    } catch {
      // not JSON
    }
    const err = new Error(body?.error ?? `http_${res.status}`);
    (err as any).body = body;
    (err as any).status = res.status;
    throw err;
  }
  return (await res.json()) as ChatResponse;
}

export function refsUsedIn(answer: string, sources: ChatSource[]): ChatSource[] {
  const used = new Set<string>();
  const sourceById = new Map(sources.map(s => [s.id, s]));
  const matches = Array.from(answer.matchAll(/\[((?:SRC|PDF)-\d+)\]/g));
  for (const m of matches) {
    if (sourceById.has(m[1])) used.add(m[1]);
  }
  return Array.from(used)
    .map(id => sourceById.get(id))
    .filter((s): s is ChatSource => !!s);
}
